/**
 * Posteingang: In-App-Benachrichtigungen (GET /notifications, gelesen markieren, löschen, springen) und
 * Erwähnungen (GET /mentions). Gelesen-Status und Zähler liegen im Store (NOTIFICATION_* Ereignisse).
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { compareSnowflakes, type MessageDto, type NotificationDto, type NotificationType } from '@lagerfeuer/shared';
import { AlarmClock, AtSign, CheckCheck, Download, Info, MessageCircle, MessagesSquare, Reply, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { Popover, type Anchor } from '../../components/Popover';
import { errorMessage } from '../../lib/api';
import { notificationsApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { useAppStore } from '../../store/app';
import { dmName } from '../../store/selectors';
import { toast } from '../../store/ui';
import { MarkdownInline } from '../messages/Markdown';
import { deleteNotification, markNotificationsRead, mergeNotifications, messageTarget, notificationTarget, relativeTime } from './notificationStore';
import './notifications.css';

const PAGE = 30;

const TYPE_ICON: Record<NotificationType, typeof AtSign> = {
  MENTION: AtSign,
  REPLY: Reply,
  DM: MessageCircle,
  THREAD_REPLY: MessagesSquare,
  REMINDER: AlarmClock,
  SYSTEM: Info,
  EXPORT_READY: Download,
  SCHEDULED_FAILED: TriangleAlert,
};

const TYPE_LABEL: Record<NotificationType, string> = {
  MENTION: 'Erwähnung',
  REPLY: 'Antwort',
  DM: 'Direktnachricht',
  THREAD_REPLY: 'Thread-Antwort',
  REMINDER: 'Erinnerung',
  SYSTEM: 'System',
  EXPORT_READY: 'Export fertig',
  SCHEDULED_FAILED: 'Geplante Nachricht fehlgeschlagen',
};

type Tab = 'notifications' | 'mentions';

export function InboxPopover({ anchor, onClose }: { anchor: Anchor; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('notifications');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const unread = useAppStore((s) => s.unreadNotifications);
  return (
    <Popover anchor={anchor} onClose={onClose} placement="bottom-end" className="lf-inbox" testId="inbox" label="Posteingang">
      <div className="lf-inbox-head">
        <div className="row">
          <h2 className="lf-inbox-title">Posteingang</h2>
          {unread > 0 && (
            <span className="badge" data-testid="inbox-unread-count" aria-label={`${unread} ungelesen`}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
          <span className="spacer" />
          {tab === 'notifications' && (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={unread === 0}
              onClick={() => void markNotificationsRead('all').catch((e: unknown) => toast.error('Fehler', errorMessage(e)))}
              data-testid="inbox-mark-all"
            >
              <CheckCheck size={14} aria-hidden="true" /> Alle gelesen
            </button>
          )}
        </div>
        <div className="tabs lf-inbox-tabs" role="tablist">
          <button type="button" role="tab" className="tab" aria-selected={tab === 'notifications'} onClick={() => setTab('notifications')} data-testid="inbox-tab-notifications">
            Benachrichtigungen
          </button>
          <button type="button" role="tab" className="tab" aria-selected={tab === 'mentions'} onClick={() => setTab('mentions')} data-testid="inbox-tab-mentions">
            Erwähnungen
          </button>
          {tab === 'notifications' && (
            <label className="checkbox small lf-inbox-filter">
              <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="inbox-unread-only" />
              Nur ungelesene
            </label>
          )}
        </div>
      </div>
      <div className="lf-inbox-body scroll" role="tabpanel">
        {tab === 'notifications' ? <NotificationList unreadOnly={unreadOnly} onClose={onClose} /> : <MentionList onClose={onClose} />}
      </div>
    </Popover>
  );
}

function NotificationList({ unreadOnly, onClose }: { unreadOnly: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const all = useAppStore((s) => s.notifications);
  const query = useInfiniteQuery({
    queryKey: [...qk.notifications, 'inbox'],
    queryFn: ({ pageParam }) => notificationsApi.list({ limit: PAGE, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => {
      if (last.length < PAGE) return undefined;
      return [...last].sort((a, b) => compareSnowflakes(a.id, b.id))[0]?.id;
    },
  });
  useEffect(() => {
    if (query.data) mergeNotifications(query.data.pages.flat());
  }, [query.data]);

  const items = [...all].sort((a, b) => compareSnowflakes(b.id, a.id)).filter((n) => !unreadOnly || !n.readAt);

  const open = (n: NotificationDto) => {
    if (!n.readAt) void markNotificationsRead([n.id]).catch(() => undefined);
    const to = notificationTarget(useAppStore.getState(), n);
    if (to) {
      onClose();
      void navigate(to);
    }
  };

  if (query.isLoading && items.length === 0) return <div className="empty"><div className="spinner" aria-label="Lädt" /></div>;
  if (query.isError && items.length === 0) return <p className="empty error-text">{errorMessage(query.error)}</p>;
  if (items.length === 0)
    return (
      <div className="empty" data-testid="inbox-empty">
        <CheckCheck size={32} aria-hidden="true" />
        <span>{unreadOnly ? 'Alles gelesen.' : 'Noch keine Benachrichtigungen.'}</span>
      </div>
    );
  return (
    <>
      <ul className="lf-inbox-list" aria-label="Benachrichtigungen">
        {items.map((n) => {
          const Icon = TYPE_ICON[n.type] ?? Info;
          return (
            <li key={n.id} className="lf-inbox-item" data-unread={!n.readAt || undefined} data-testid="inbox-item" data-id={n.id}>
              <button type="button" className="lf-inbox-item-main" onClick={() => open(n)} data-testid="inbox-item-open">
                <span className="lf-inbox-icon">
                  {n.actor ? <Avatar user={n.actor} size={36} /> : <span className="lf-inbox-type"><Icon size={18} aria-hidden="true" /></span>}
                  {n.actor && (
                    <span className="lf-inbox-type-badge" aria-hidden="true">
                      <Icon size={11} />
                    </span>
                  )}
                </span>
                <span className="lf-inbox-text">
                  <span className="lf-inbox-item-title">
                    <span className="sr-only">{TYPE_LABEL[n.type]}: </span>
                    {n.title}
                  </span>
                  {n.body && <span className="lf-inbox-item-body">{n.body}</span>}
                  <span className="lf-inbox-time faint small">
                    <time dateTime={n.createdAt}>{relativeTime(n.createdAt)}</time>
                    {!n.readAt && <span className="sr-only"> · ungelesen</span>}
                  </span>
                </span>
              </button>
              <span className="lf-inbox-item-actions">
                {!n.readAt && (
                  <button type="button" className="icon-btn" aria-label="Als gelesen markieren" onClick={() => void markNotificationsRead([n.id]).catch(() => undefined)} data-testid="inbox-item-read">
                    <CheckCheck size={16} />
                  </button>
                )}
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Benachrichtigung löschen"
                  onClick={() => void deleteNotification(n).catch((e: unknown) => toast.error('Löschen fehlgeschlagen', errorMessage(e)))}
                  data-testid="inbox-item-delete"
                >
                  <Trash2 size={16} />
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      {query.hasNextPage && (
        <div className="lf-inbox-more">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage} data-testid="inbox-load-more">
            Ältere laden
          </button>
        </div>
      )}
    </>
  );
}

function MentionList({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [everyone, setEveryone] = useState(true);
  const [roles, setRoles] = useState(true);
  const query = useInfiniteQuery({
    queryKey: [...qk.mentions, everyone, roles],
    queryFn: ({ pageParam }) => notificationsApi.mentions({ limit: 25, before: pageParam, everyone, roles }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length < 25 ? undefined : [...last].sort((a, b) => compareSnowflakes(a.id, b.id))[0]?.id),
  });
  const items = (query.data?.pages.flat() ?? []).sort((a, b) => compareSnowflakes(b.id, a.id));
  return (
    <>
      <div className="row lf-inbox-filters">
        <label className="checkbox small">
          <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} data-testid="mentions-everyone" />
          @everyone/@here
        </label>
        <label className="checkbox small">
          <input type="checkbox" checked={roles} onChange={(e) => setRoles(e.target.checked)} data-testid="mentions-roles" />
          Rollen
        </label>
      </div>
      {query.isLoading && <div className="empty"><div className="spinner" aria-label="Lädt" /></div>}
      {query.isError && <p className="empty error-text">{errorMessage(query.error)}</p>}
      {!query.isLoading && items.length === 0 && (
        <div className="empty" data-testid="mentions-empty">
          <AtSign size={32} aria-hidden="true" />
          <span>Keine Erwähnungen.</span>
        </div>
      )}
      <ul className="lf-inbox-list" aria-label="Erwähnungen">
        {items.map((m) => (
          <MentionItem
            key={m.id}
            message={m}
            onOpen={() => {
              onClose();
              void navigate(messageTarget(useAppStore.getState(), m));
            }}
          />
        ))}
      </ul>
      {query.hasNextPage && (
        <div className="lf-inbox-more">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage} data-testid="mentions-load-more">
            Ältere laden
          </button>
        </div>
      )}
    </>
  );
}

function MentionItem({ message: m, onOpen }: { message: MessageDto; onOpen: () => void }) {
  const where = useAppStore((s) => {
    const ch = s.channels[m.channelId];
    if (!ch) return null;
    if (!ch.serverId) return dmName(ch, s.me?.id);
    const server = s.servers[ch.serverId];
    return `#${ch.name ?? ''}${server ? ` · ${server.name}` : ''}`;
  });
  const author = m.author;
  const name = m.overrideName || m.member?.nickname || author?.displayName || author?.username || 'Unbekannt';
  return (
    <li className="lf-inbox-item" data-testid="mention-item">
      <button type="button" className="lf-inbox-item-main" onClick={onOpen} data-testid="mention-item-open">
        <span className="lf-inbox-icon">
          <Avatar user={author ?? { id: '0', username: name, displayName: null, avatarUrl: m.overrideAvatarUrl, avatarAnimated: false }} size={36} />
        </span>
        <span className="lf-inbox-text">
          <span className="lf-inbox-item-title">
            {name}
            {where && <span className="faint small"> in {where}</span>}
          </span>
          <span className="lf-inbox-item-body lf-inbox-markdown">
            <MarkdownInline content={m.content} serverId={m.serverId} />
          </span>
          <span className="lf-inbox-time faint small">
            <time dateTime={m.createdAt}>{relativeTime(m.createdAt)}</time>
          </span>
        </span>
      </button>
    </li>
  );
}
